import { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { NavLink } from "react-router-dom";

//components
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import { Chip, Fab, Grid, Icon, Tooltip as MuiTooltip } from "@mui/material";
import InputDate from 'Components/InputDate';
import moment from 'moment';
import { Button, Tooltip } from '@gpstrackit/react-controls';
import { NotificationManager } from 'Components/Notifications';
import Filters from 'Components/Filters';


//Helpers
import { getMinDateLimitBehaviour, getDateFormatted, getRssiSignalIcon, getHDopColorLabel } from 'Core/data/Helpers';
import { selectEntityState } from 'Core/common/redux/infiniteScrollSlice';
import { clientQuery } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';

import {
    systemAlertContacsOpenModal,
    systemAlertLastReadingInfoOpenModal,
    systemAlertGraphicViewOpenModal,
    getDeviceReadingOpenModal,
    changeListOpenModal
} from 'Redux/actions'

//Views
import LastReadingInfo from 'Modules/accounts/.components/systemAlertLogsTab/.components/lastReadingInfo';
import GraphicView from 'Modules/accounts/.components/systemAlertLogsTab/.components/graphicView';
import DeviceReadingInfo from 'Modules/rawData/.components/tab/.components/details';
import Contacts from 'Modules/accounts/.components/systemAlertLogsTab/.components/contacts';
import GeneralDetails from 'Modules/activities/.components/generalDetails';
import ChangeList from 'Modules/activities/.components/changeList';
//Styles
import "./index.scss"

const RANGE_MODE_ROWS = 50;

const SystemAlertLogsTab = (props) => {
    const dispatch = useDispatch();
    const { height, device, company, className } = props;
    const { messages } = useIntl();
    const tableRef = useRef();

    const contactsOpenModal = useSelector(state => state.systemAlertContactsRedux.openModal);
    const lastReadingInfoOpenModal = useSelector(state => state.systemAlertLastReadingInfoRedux?.openModal);
    const graphicViewOpenModal = useSelector(state => state.systemAlertGraphicViewRedux?.openModal);
    const deviceReadingInfoOpenModal = useSelector(state => state.deviceReadingRedux.openModal);
    const generalDetailsOpenModal = useSelector(state => state.generalDetailsRedux.openModal);
    const changeListOpenModalA = useSelector(state => state.changeListRedux.openModal);
    const entityState = useSelector(selectEntityState('system-alert-logs'));

    const current_week = moment().startOf('week');
    const tempStartDate = new Date(current_week.toString());
    const [startDate, setStartDate] = useState(tempStartDate);
    const [endDate, setEndDate] = useState(new Date());
    const [beforeRangeDate, setBeforeRangeDate] = useState({
        start: tempStartDate,
        end: endDate
    })

    const [filters, setFilters] = useState({
        startDate: tempStartDate,
        endDate: endDate
    })

    const [last, setLast] = useState({ id: '20', name: '20' });
    const [type, setType] = useState('last');

    const [columns, setColumns] = useState([
        {
            key: "id",
            title: messages['system_alert_logs_tab_id'],
            field: "id",
            show: true,
            resize: true,
            width: 150,
            sort: true,
            export: true,
            functionString: 'return row?.id',
            code: "system_alert_logs_tab_id",
            render: (item) => {
                let newDate = item?.createdOn
                if (item?.createdOn) {
                    let date = new Date(item?.createdOn);
                    date = date.setSeconds(60)
                    newDate = moment(date).utc().format()
                }
                return (
                    <div style={{ display: 'flex', width: 150, justifyContent: 'space-between' }}>
                        <span>{item?.id}</span>
                        <Tooltip title={messages['common_show_changes']}>
                            <Icon onClick={() => {
                                //dispatch(generalAuditLogsOpenModal(true, "SystemAlert", item?.systemAlert?.id, company?.id, null, newDate))
                                dispatch(changeListOpenModal(true, { entityName: "SystemAlert", entityId: item?.systemAlert?.id, compId: company?.id }))
                            }} style={{ cursor: 'pointer' }}>info</Icon>
                        </Tooltip>
                    </div>
                )
            }
        },
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['system_alert_logs_tab_created_on'],
            show: true,
            resize: true,
            sort: true,
            width: 250,
            export: true,
            functionString:
                `
              function formatDate(date) {
                  let dateToformat = moment(date)
                  let formatted = moment(dateToformat).format("YYYY/MM/DD HH:mm:ss")
                  return formatted;
                }
              return row?.createdOn ? formatDate(row?.createdOn) : ''
            `,
            code: "system_alert_logs_tab_created_on",
            render: (item) => {
                return <span>{item?.createdOn && getDateFormatted(item?.createdOn, "date", "datetime", null, item?.unit?.timezone)}</span>
            }
        },
        {
            key: "unitTime",
            field: "unitTime",
            title: messages['system_alert_logs_tab_unit_time'],
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString:
                `
              function formatDate(date) {
                  let dateToformat = moment(date)
                  let formatted = moment(dateToformat).format("YYYY/MM/DD HH:mm:ss")
                  return formatted;
                }
              return row?.unitTime ? formatDate(row?.unitTime) : ''
            `,
            code: "system_alert_logs_tab_unit_time",
            render: (item) => {
                return <span>{item?.unitTime && getDateFormatted(item?.unitTime, "date", "datetime", null, item?.unit?.timezone)}</span>
            }
        },
        {
            key: "systemAlert_type",
            title: messages['system_alert_logs_tab_type'],
            field: "systemAlert.type",
            show: true,
            resize: true,
            width: 150,
            sort: true,
            export: true,
            functionString: 'return row?.systemAlert?.type',
            code: "system_alert_logs_tab_type",
            render: (item) => {
                return <span>{item?.systemAlert?.type}</span>
            }
        },
        {
            key: "systemAlert_name",
            title: messages['system_alert_logs_tab_name'],
            field: "systemAlert.name",
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString: 'return row?.systemAlert?.name',
            code: "system_alert_logs_tab_name",
            render: (item) => {
                return <span>{item?.systemAlert?.name}</span>
            }
        },
        {
            key: "details",
            title: messages['system_alert_logs_tab_details'],
            field: "details",
            show: true,
            resize: true,
            width: 300,
            export: true,
            sort: true,
            functionString: 'return row?.details',
            code: "system_alert_logs_tab_details",
            render: (item) => {
                return <span>{item?.details}</span>
            }
        },
        {
            key: "systemAlert_severity",
            title: messages['system_alert_logs_tab_severity'],
            field: "systemAlert.severity",
            show: true,
            resize: true,
            width: 150,
            sort: true,
            export: true,
            functionString: 'return row?.systemAlert?.severity',
            code: "system_alert_logs_tab_severity",
            render: (item) => {
                return <span>{item?.systemAlert?.severity}</span>
            }
        },
        {
            key: "unit_label",
            title: messages['system_alert_logs_tab_unit_label'],
            field: "unit.label",
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString: 'return row?.unit?.label',
            code: "system_alert_logs_tab_unit_label",
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <div style={{ display: 'flex', alignItems: 'center', width: 230, justifyContent: 'space-between' }}>
                                <NavLink to={`/app/devices/overview/${item?.unit?.id}`} style={{ color: 'black' }}>
                                    <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.unit?.label}</span>
                                </NavLink>
                                {
                                    item?.unit?.id && item?.unit?.label &&
                                    <Tooltip title={messages['system_alert_logs_tab_show_last_reading_info']}>
                                        <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                                            dispatch(systemAlertLastReadingInfoOpenModal(true, { accountId: company?.id, deviceId: item?.unit?.id }))
                                        }}>
                                            remove_red_eye
                                        </Icon>
                                    </Tooltip>
                                }
                            </div>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "linked_unit_label",
            title: messages['system_alert_logs_tab_linked_unit_label'],
            field: "unit.label",
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString: 'return row?.linkedUnit?.label',
            code: "system_alert_logs_tab_linked_unit_label",
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <div style={{ display: 'flex', alignItems: 'center', width: 230, justifyContent: 'space-between' }}>
                                <NavLink to={`/app/devices/overview/${item?.linkedUnit?.id}`} style={{ color: 'black' }}>
                                    <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.linkedUnit?.label}</span>
                                </NavLink>
                                {
                                    item?.linkedUnit?.id &&
                                    <Tooltip title={messages['system_alert_logs_tab_show_last_reading_info']}>
                                        <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                                            dispatch(systemAlertLastReadingInfoOpenModal(true, { accountId: company?.id, deviceId: item?.linkedUnit?.id }))
                                        }}>
                                            remove_red_eye
                                        </Icon>
                                    </Tooltip>
                                }
                            </div>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "driverName",
            title: messages['system_alert_logs_tab_driver_name'],
            field: "driverName",
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString: 'return row?.driverName',
            code: "system_alert_logs_tab_driver_name",
            render: (item) => {
                return <span>{item?.driverName ? item?.driverName !== "NA" ? item?.driverName : "" : ""}</span>
            }
        },
        {
            key: "latitude",
            title: messages['system_alert_logs_tab_latitude'],
            field: "latitude",
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString: 'return row?.latitude',
            code: "system_alert_logs_tab_latitude",
            render: (item) => {
                return <span>{item?.latitude}</span>
            }
        },
        {
            key: "longitude",
            title: messages['system_alert_logs_tab_longitude'],
            field: "longitude",
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString: 'return row?.longitude',
            code: "system_alert_logs_tab_longitude",
            render: (item) => {
                return <span>{item?.longitude}</span>
            }
        },
        {
            key: "address",
            title: messages['system_alert_logs_tab_address'],
            field: "address",
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString: 'return row?.address',
            code: "system_alert_logs_tab_address",
            render: (item) => {
                return <span>{item?.address}</span>
            }
        },
        {
            key: "hdop",
            title: messages['system_alert_logs_tab_hdop'],
            field: "jsonData",
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString: `
            
            function formatHDop(data) {
                let jsonData = data ? JSON.parse(JSON.parse(data)) : null
                let hdop = "";
                
                if (jsonData?.hDop) {
                    hdop = jsonData?.hDop > 0 ? jsonData?.hDop / 100 : 0;
                }
                return hdop;
              }
            return row?.jsonData ? formatHDop(row?.jsonData) : ''`,

            code: "system_alert_logs_tab_hdop",
            render: (item) => {
                let jsonData = item?.jsonData ? JSON.parse(JSON.parse(item?.jsonData)) : null
                if (jsonData?.hDop) {
                    let hdop = getHDopColorLabel(jsonData?.hDop, messages)
                    return <Chip title={hdop.value}
                        style={{
                            marginRight: 5,
                            backgroundColor: hdop.color,
                            color: '#ffff'
                        }}
                        label={hdop.label}
                    />
                }
                return <span>{""}</span>
            }
        },
        {
            key: "rssi",
            title: messages['system_alert_logs_tab_rssi'],
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString:
                `
            function formatRssi(data) {
                let jsonData = data ? JSON.parse(JSON.parse(data)) : null
                let rssi = jsonData?.rssi || "";
                return rssi;
              }
            return row?.jsonData ? formatRssi(row?.jsonData) : ''`,
            code: "system_alert_logs_tab_rssi",
            render: (item) => {
                let jsonData = item?.jsonData ? JSON.parse(JSON.parse(item?.jsonData)) : null
                if (jsonData?.rssi)
                    return getRssiSignalIcon(jsonData?.rssi, messages)
                else
                    return <span>{""}</span>
            }
        },
        {
            key: "satelliteCount",
            title: messages['system_alert_logs_tab_satellite_count'],
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString:
                `
            function formatSatelliteCount(data) {
                let jsonData = data ? JSON.parse(JSON.parse(data)) : null
                let satelliteCount = jsonData?.satelliteCount || "";
                return satelliteCount;
              }
            return row?.jsonData ? formatSatelliteCount(row?.jsonData) : ''`,
            code: "system_alert_logs_tab_satellite_count",
            render: (item) => {
                let jsonData = item?.jsonData ? JSON.parse(JSON.parse(item?.jsonData)) : null
                return <span>{jsonData?.satelliteCount || ""}</span>
            }
        },
        {
            key: "landmarkName",
            title: messages['system_alert_logs_tab_landmark_name'],
            field: "landmarkName",
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString: 'return row?.landmarkName',
            code: "system_alert_logs_tab_landmark_name",
            render: (item) => {
                return <span>{item?.landmarkName}</span>
            }
        },
        {
            key: "deviceReadingId",
            title: messages['system_alert_logs_tab_device_reading_id'],
            field: "deviceReadingId",
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString: 'return row?.deviceReadingId',
            code: "system_alert_logs_tab_device_reading_id",
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <div style={{ display: 'flex', alignItems: 'center', width: 230, justifyContent: 'space-between' }}>
                                <span>{item?.deviceReadingId}</span>
                                {
                                    item?.unit?.id && item?.deviceReadingId &&
                                    <Tooltip title={messages['system_alert_logs_tab_show_device_reading_info']}>
                                        <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                                            dispatch(getDeviceReadingOpenModal(true, { id: item?.deviceReadingId, deviceId: item?.unit?.id }))
                                        }}>
                                            remove_red_eye
                                        </Icon>
                                    </Tooltip>
                                }
                            </div>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "contacts",
            title: messages['system_alert_logs_tab_contacts'],
            field: "contacts",
            show: true,
            resize: true,
            width: 50,
            export: true,
            functionString: 'return row?.contacts',
            code: "system_alert_logs_tab_contacts",
            render: (item) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        {
                            item?.contacts &&
                            <Icon style={{ cursor: 'pointer' }} onClick={() => dispatch(systemAlertContacsOpenModal(true, item?.contacts, { id: item.id, start: filters.startDate, end: filters.endDate }))}>people</Icon>
                        }
                    </div>
                )
            }
        },
    ])
    const [searchFields, setSearchFields] = useState(["id", "unit_label", "unit_esn", "systemAlert_name", "systemAlert_type", "systemAlert_severity", "details"]);

    const onChangeStartEndDate = ({ start, end }) => {
        const newStart = start && new Date(start);
        const newEnd = end && new Date(end);

        let fltrs = filters;
        fltrs.startDate = newStart;
        fltrs.endDate = newEnd;
        // Set New Date Range
        if (start && end) {
            const newDateRange = {
                start,
                end,
            };
            setBeforeRangeDate(newDateRange)
        }
        setFilters({ ...fltrs });
    }

    const onCloseSelectDate = () => {
        const { start, end } = beforeRangeDate;
        if (start && end && !endDate) {
            let fltrs = filters;
            fltrs.startDate = start;
            fltrs.endDate = end;
            setFilters({ ...fltrs });
        }
    }

    const onChange = (value) => {
        const { start, end } = value;
        onChangeStartEndDate({
            start,
            end
        })
    };

    const getRange = () => {
        const tz = company?.timezone || device?.timezone || moment.tz.guess();
        let startDatetz = moment.tz(filters.startDate, tz).startOf("day");
        let endDatetz = moment.tz(filters.endDate, tz).endOf("day");
        let start = startDatetz.clone().utc();
        let endUtc = endDatetz.clone().utc();
        return {
            fields: ["unitTime"],
            GTE: start.format(),
            LTE: endUtc.format()
        }
    }

    const applyFilters = (paramType, paramRows, paramLast) => {
        let currentType = paramType || type;

        let currentNewConditions = tableRef?.current?.conditions;
        let deleteCondition = -1;
        let field = device ? "unitId" : "companyId";
        // "Last N" mode: Use the selected limit value (5/10/20/etc) as rows
        // "Range" mode: Use paramRows or default pagination
        let currentRows = currentType === 'last' 
            ? parseInt(paramLast?.id || last?.id, 10) 
            : paramRows || RANGE_MODE_ROWS;
        let sort = null

        deleteCondition = currentNewConditions?.findIndex((item) => item?.fields && item?.fields[0] === field);
        currentNewConditions && deleteCondition > -1 && currentNewConditions.splice(deleteCondition, 1);

        if (currentType === 'last') {
            currentNewConditions && currentNewConditions.push({
                fields: [field], terms: [device ? device.id : company.id], exact: true, conditionOperator: "AND"
            });

            sort = [{ field: "id", order: "DESC" }]

        } else if (filters.startDate && filters.endDate) {
            currentNewConditions && currentNewConditions.push({
                fields: [field], terms: [device ? device.id : company.id], exact: true, range: getRange(), conditionOperator: "AND"
            });
        } else {
            NotificationManager.warning(
                messages['message_outbox_tab_date_range'],
                messages['system_alert_logs_tab_title'],
                5000,
            );
        }

        tableRef?.current?.getData({ newRows: currentRows, newSort: sort, newConditions: currentNewConditions, showTotalCount: currentType !== 'last' });
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // DATAVIEW HANDLERS & CONFIGURATION
    // Extracted from JSX for readability
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Handle condition changes from search/filters
     */
    const handleChangeConditions = (tags, conditions, total, newColumns, rows, sort, key, fromPreferences, restoreOnly) => {
        let currentConditions = {}
        let currentTags = {}
        let currentType = type
        let currentLast = last 
        if (tags) {
            currentConditions = tableRef?.current?.conditions || [];
            currentTags = tags
        }
        if (conditions) {
            currentConditions = conditions || [];
            currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
            if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);

            if (fromPreferences) {
                // Restore from saved preferences: check for range or last mode
                let rangeCondition = currentConditions.find(x => x.range)
                if (rangeCondition) {

                    let fltrs = filters;
                    // Reverse getRange() UTC conversion: UTC → company timezone → extract calendar date → local midnight.
                    // Without this, new Date("2026-02-01T06:00:00Z") in a western-hemisphere browser
                    // would display as Jan 31 (previous day) because Date renders in browser-local time.
                    const tz = company?.timezone || device?.timezone || moment.tz.guess();

                    fltrs.startDate = new Date(moment.utc(rangeCondition.range.GTE).tz(tz).startOf("day").format("YYYY-MM-DD[T]HH:mm:ss"));
                    fltrs.endDate = new Date(moment.utc(rangeCondition.range.LTE).tz(tz).endOf("day").format("YYYY-MM-DD[T]HH:mm:ss"));
                    setFilters({ ...fltrs });
                    setType('range')
                    currentType = 'range' 
                    
                    // Sync InputDate picker state with loaded preferences
                    setStartDate(fltrs.startDate);
                    setEndDate(fltrs.endDate);
                    setBeforeRangeDate({
                        start: fltrs.startDate,
                        end: fltrs.endDate
                    });
                } else if (rows) {
                    // No range condition → "Last N" mode.
                    // DVIS saves `rows` via getData({ newRows }) and passes it back on restore.
                    setType('last');
                    currentType = 'last';
                    setLast({ id: String(rows), name: String(rows) });
                    currentLast = { id: String(rows), name: String(rows) };
                }
            } else {
                // Non-preferences condition change (e.g., tags search)
                let rangeCondition = currentConditions.find(x => x.range)
                if (rangeCondition) {
                    let fltrs = filters;
                    const tz = company?.timezone || device?.timezone || moment.tz.guess();

                    fltrs.startDate = new Date(moment.utc(rangeCondition.range.GTE).tz(tz).startOf("day").format("YYYY-MM-DD[T]HH:mm:ss"));
                    fltrs.endDate = new Date(moment.utc(rangeCondition.range.LTE).tz(tz).endOf("day").format("YYYY-MM-DD[T]HH:mm:ss"));
                    setFilters({ ...fltrs });
                    setType('range')
                    currentType = 'range' 
                    
                    setStartDate(fltrs.startDate);
                    setEndDate(fltrs.endDate);
                    setBeforeRangeDate({
                        start: fltrs.startDate,
                        end: fltrs.endDate
                    });
                }
            }

        }

        // restoreOnly: data already persisted in redux, just sync UI states — skip getData
        if (restoreOnly) return;

        currentConditions = currentConditions.filter((item) => item?.fields && item?.fields[0] !== 'id' && item?.fields[0] !== 'unitId' && item?.fields[0] !== 'companyId');

        // Numeric IDs (selected from dropdown) → exact match on ID field only
        let tagsById = currentTags.filter((item) => typeof item.id === "number");
        if (tagsById?.length > 0) {
            let currentIds = tagsById.map((item) => (item.id));
            if (currentIds.length > 0) {
                currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
            }
        }

        // String IDs (typed by user) → fuzzy search across multiple fields
        let tagsByLabel = currentTags.filter((item) => typeof item.id === "string");
        if (tagsByLabel.length > 0) {
            let currentLabels = tagsByLabel.map((item) => (item.id))
            if (currentLabels.length > 0) {
                currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
            }
        }
        sort = sort

        if (currentType !== 'last') {
            currentConditions && currentConditions.push({ fields: ["companyId"], terms: [company?.id], exact: true, range: device ? null : getRange(), conditionOperator: "AND" });
        } else {
            // "Last N" mode: Use limit parameter instead of conditions.last
            // The limit is passed via getData({ newRows: N }) - no `last` property in conditions
            currentConditions && currentConditions.push({ fields: ["companyId"], terms: [company?.id], exact: true, conditionOperator: "AND" });
            sort = [{ field: "id", order: "DESC" }]
        }
        if (device) {
            if (currentType !== 'last') {
                currentConditions && currentConditions.push({ fields: ["unitId"], terms: [device?.id], exact: true, range: getRange(), conditionOperator: "AND" });
            } else {
                // "Last N" mode: Use limit parameter instead of conditions.last
                currentConditions && currentConditions.push({ fields: ["unitId"], terms: [device?.id], exact: true, conditionOperator: "AND" });
                sort = [{ field: "id", order: "DESC" }]
            }
        }

        // Determine effective rows:
        // - "Last N" mode: Use the selected limit value (5/10/20/etc)
        // - "Range" mode: Use 50 rows for pagination (ignore any smaller preference value)
        const effectiveRows = currentType === 'last' 
            ? parseInt(currentLast?.id || last?.id, 10) 
            : RANGE_MODE_ROWS

        if (!currentConditions && tags) {
            tableRef?.current?.getData({ newRows: effectiveRows, newConditions: currentConditions, newColumns: newColumns });
        } else {
            tableRef?.current?.getData({ newRows: effectiveRows, newSort: sort, newConditions: currentConditions, newColumns: newColumns });
        }
    };

    /**
     * Handle column changes (reorder, resize, visibility)
     */
    const handleChangeColumns = (items) => setColumns(items);

    /**
     * Extra button component - renders custom filter UI and chart button
     */
    const renderExtraButton = () => {
        return (
            <div style={{ /* width: "100%",  */display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Filters
                    onChangeFilters={() => applyFilters()}
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    setBeforeRangeDate={setBeforeRangeDate}
                    beforeRangeDate={beforeRangeDate}
                    onChange={onChange}
                    onChangeStartEndDate={onChangeStartEndDate}
                    onCloseSelectDate={onCloseSelectDate}
                    last={last}
                    setLast={setLast}
                    type={type}
                    setType={(value) => {
                        setType(value)
                        let cRows = null;
                        let cLast = last || null
                        if (value === 'range') {
                            tableRef?.current?.setRows(RANGE_MODE_ROWS);
                            cRows = RANGE_MODE_ROWS
                        }

                        applyFilters(value, cRows, cLast)
                    }}
                    extraButtonFilter={() => (
                        <Button
                            className="btn btn-dark-blue"
                            onClick={() => dispatch(systemAlertGraphicViewOpenModal(true))}
                            style={{ display: 'flex', alignItems: 'center', marginLeft: 10, padding: "8px 12px" }}
                        >
                            <Icon style={{ marginRight: 5 }}>pie_chart</Icon>
                            <span>{messages['system_alert_logs_graphic_view_button'] || 'View Chart'}</span>
                        </Button>
                    )}
                />
            </div>
        )
    };

    const escapeCsvValue = (val) => {
        const str = val === undefined || val === null ? '' : String(val);
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const evaluateFunctionString = (functionString, row) => {
        if (!functionString) return '';
        try {
            return new Function('row', 'moment', functionString)(row, moment);
        } catch (error) {
            return '';
        }
    };

    const buildDynamicFields = (columnsToExport, baseQuery, includeHidden = false) => {
        let params = baseQuery;
        const columnsToUse = includeHidden
            ? columnsToExport.filter((item) => item.export)
            : columnsToExport.filter((item) => item.show);

        columnsToUse.map((column) => {
            params += " "
            let fields = column.field && column.field.split(",") || []
            fields.map(f => {
                let array = f.split(".")
                if (array.length > 1) {
                    let text = "";
                    let closeText = "";
                    if (params.includes(array[0])) {
                        let divider = params.split(`${array[0]} { `)
                        params = divider[0] + ` ${array[0]} { ` + `${array[1]} ` + divider[1]
                    } else {
                        for (let i = 0; i < array.length; i++) {
                            if (!params.includes(array[0])) {
                                text += array[i]
                            }
                            if (i < array.length - 1) {
                                text += " { "
                                closeText += " } "
                            }
                        }
                    }
                    params += text + closeText
                }
                else params += array[0]
            })
        })
        return params;
    };

    const buildGraphQLQuery = ({ functionName, conditionsType, sortType, fields, extraFields = '' }) => {
        const sortParam = sortType ? `, $sort: [${sortType}]` : '';
        const sortArg = sortType ? ', sort: $sort' : '';
        return `
            query ${functionName} ($conditions: [${conditionsType}]${sortParam}, $limit: Int, $offset: Int) {
                result: ${functionName} (conditions: $conditions${sortArg}, limit: $limit, offset: $offset) {
                    items {
                        ${fields}
                    }
                    ${extraFields}
                }
            }
        `;
    };

    const generateCsvFromRows = (rows, columnsVisibility) => {
        const exportColumns = (columns || []).filter((col) =>
            columnsVisibility === 'All' ? col.export : col.export && col.show
        );

        const headerRow = exportColumns.map((col) => col.title || col.field);
        const csvLines = [headerRow.map(escapeCsvValue).join(',')];

        rows.forEach((row) => {
            const line = exportColumns.map((col) =>
                escapeCsvValue(evaluateFunctionString(col.functionString, row))
            );
            csvLines.push(line.join(','));
        });

        const csvContent = csvLines.join('\n');
        const date = new Date();
        const fileName = `systemalertlog_${date.getFullYear()}${date.getMonth()}${date.getDay()}${date.getHours()}${date.getMinutes()}.csv`;

        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([csvContent], { type: 'text/csv;encoding:utf-8' }));
        a.setAttribute('download', fileName);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        NotificationManager.success(`Exported ${rows.length} rows`, 'Export Complete', 3000);
    };

    const exportLocalCsv = async (currentType, columnsVisibility) => {
        NotificationManager.info('Generating export...', 'Export Started', 2000);
        let rowsToExport = [];

        if (columnsVisibility === 'All') {
            try {
                const baseQuery = "id unit { id timezone } systemAlert { id name createdOn updatedOn } updatedOn jsonData";
                const dynamicFields = buildDynamicFields(columns, baseQuery, true);

                const query = buildGraphQLQuery({
                    functionName: 'searchSystemAlertLogs',
                    conditionsType: 'SystemAlertLogSearchInput',
                    sortType: 'SystemAlertLogSortInput',
                    fields: dynamicFields,
                    extraFields: 'total',
                });

                const sanitizedConditions = (tableRef?.current?.conditions || [])
                    ?.map(({ tags, account, ...condition }) => condition) || [];

                const variables = {
                    limit: entityState?.items?.length || 500,
                    offset: 0,
                    sort: [{ field: "updatedOn", order: "DESC" }],
                    conditions: sanitizedConditions,
                };

                const response = await clientQuery(query, variables, endpoints.GRAPHQL_GENERAL);
                rowsToExport = response?.result?.items || [];
            } catch (error) {
                console.error('Error fetching complete data for export:', error);
                NotificationManager.error('Error exporting data', 'Export Failed', 5000);
                return;
            }
        } else {
            rowsToExport = entityState?.items || [];
        }

        if (rowsToExport.length === 0) {
            NotificationManager.warning('No data to export', 'Export Warning', 5000);
            return;
        }

        generateCsvFromRows(rowsToExport, columnsVisibility);
    };

    return (
        <div className="system-alert-log" style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                ref={tableRef}
                columns={columns}
                name={`pm-system-alert-logs-tab-${device ? "device" : "company"}`}
                entity="system-alert-logs"
                infiniteScroll={false}
                selectionConfig={{ showCounter: false, disabled: true }}
                showTotalCount={type !== 'last'}
                graphqlConfig={{
                    function: "searchSystemAlertLogs",
                    sortType: "SystemAlertLogSortInput",
                    conditionsType: "SystemAlertLogSearchInput",
                    customQuery: "id unit { id timezone } systemAlert { id name createdOn updatedOn } updatedOn jsonData",
                    dynamicQuery: true,
                }}
                searchConfig={{
                    optionValue: "id",
                    optionLabel: "id",
                    conditions: searchFields,
                    fields: searchFields,
                    extraField: "systemAlert { id name } unit { id label }",
                    fixBar: true,
                    custom: { fields: ["companyId"], terms: [company?.id], exact: true, range: getRange(), conditionOperator: "AND" },
                    // Conditional label: Devices tab shows id + alert name, Accounts tab shows id + unit + alert name
                    // Typed tags (no API data) return as-is, selected options get custom formatting
                    openCustomOptions: (item) => {
                        // Typed tags have no systemAlert data - return label as-is
                        if (!item.systemAlert) {
                            return item.label || item.id;
                        }
                        // Selected from dropdown - apply custom formatting
                        const alertName = item.systemAlert?.name || '';
                        if (device) {
                            // Devices Overview: id + systemAlert name
                            return `#${item.id}${alertName ? ` - ${alertName}` : ''}`;
                        } else {
                            // Accounts Overview: id + unit label + systemAlert name
                            const unitLabel = item.unit?.label || '';
                            return `#${item.id}${unitLabel ? ` | ${unitLabel}` : ''}${alertName ? ` - ${alertName}` : ''}`;
                        }
                    }
                }}
                exportConfig={{
                    fileName: "systemalertlog",
                    generate: type === 'last' ? exportLocalCsv : undefined,
                }}
                uiConfig={{
                    hideSplit: true,
                    hideFilters: true,
                    hidePagination: type === "last",
                    hideAllInPagination: true,
                    topHeight: props?.single ? 286 : 200,
                    height: height && height + "px" || "500px",
                }}
                defaultsConfig={{
                    rowsPerPage: 25,
                    valuesPerPage: [10, 25, 50]
                }}
                onChangeConditions={handleChangeConditions}
                onChangeColumns={handleChangeColumns}
                tableButtons={["export", 'orderColumn']}
                extraButton={renderExtraButton}
            />
            {contactsOpenModal && <Contacts />}
            {lastReadingInfoOpenModal && <LastReadingInfo />}
            {deviceReadingInfoOpenModal && <DeviceReadingInfo />}
            {changeListOpenModalA && <ChangeList company={company} />}
            {generalDetailsOpenModal && <GeneralDetails />}
            <GraphicView 
                company={company} 
                device={device} 
                type={type} 
                last={last} 
                filters={filters} 
            />
        </div>
    )
}

export default SystemAlertLogsTab
